/**
 * Revision API Hook
 *
 * Handles server-side rescore calls with debouncing and error handling.
 * Non-blocking: errors fall back to client-side estimation.
 *
 * Extracted from useRevisionTracking.ts for single-responsibility clarity.
 */

import { useCallback, useRef, useState, useEffect } from 'react';
import { rescoreAnalysis, type DramaCategory } from '@dramadetector/api-client';
import { trackEvent, type UserTier } from '@/lib/analytics/events';
import { calculateRevisedScore, RESCORE_DEBOUNCE_MS } from './useScoreCalculation';
import { normalizeError, type TrackingErrorHandler } from './revision-utils';

interface UseRevisionApiOptions {
  analysisId: string;
  originalScore: number;
  context?: string;
  enabled: boolean;
  tier: UserTier;
  acceptedIssueCount: number;
  totalIssues: number;
  /** Mutable ref to write server score into (from useScoreCalculation) */
  serverScoreRef: React.RefObject<number | null>;
  /** Mutable ref to write server category into (from useScoreCalculation) */
  serverCategoryRef: React.RefObject<DramaCategory | null>;
  onTrackingError?: TrackingErrorHandler;
}

interface UseRevisionApiReturn {
  /** Trigger a debounced server-side rescore */
  triggerRescore: (currentRevisedText: string) => void;
  /** Whether a server-side rescore is in progress */
  isRescoring: boolean;
}

/**
 * Hook for server-side rescore API calls.
 *
 * DD-162: Debounced rescore that updates server score refs.
 * Non-blocking: errors fall back to client-side estimate and report to Sentry.
 * Rate-limited server-side: max 1 call per 5 seconds per analysis.
 */
export function useRevisionApi({
  analysisId,
  originalScore,
  context,
  enabled,
  tier,
  acceptedIssueCount,
  totalIssues,
  serverScoreRef,
  serverCategoryRef,
  onTrackingError,
}: UseRevisionApiOptions): UseRevisionApiReturn {
  const [isRescoring, setIsRescoring] = useState(false);
  const isRescoringRef = useRef(false);
  const rescoreTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup rescore timer on unmount
  useEffect(() => {
    return () => {
      if (rescoreTimerRef.current) {
        clearTimeout(rescoreTimerRef.current);
      }
    };
  }, []);

  /**
   * Trigger a server-side rescore (DD-162)
   *
   * Debounced: waits RESCORE_DEBOUNCE_MS after last call to avoid rapid-fire requests.
   * Non-blocking: errors fall back to client-side estimation and report to Sentry.
   */
  const triggerRescore = useCallback(
    (currentRevisedText: string) => {
      // Clear existing timer first (debounce) — ensures no orphaned timers when disabled
      if (rescoreTimerRef.current) {
        clearTimeout(rescoreTimerRef.current);
      }

      if (!enabled) return;

      rescoreTimerRef.current = setTimeout(async () => {
        // Guard: Don't rescore if already in progress (use ref to avoid stale closure)
        if (isRescoringRef.current) return;

        isRescoringRef.current = true;
        setIsRescoring(true);

        try {
          const { data, error } = await rescoreAnalysis({
            analysisId,
            revisedMessage: currentRevisedText,
            originalScore,
            context,
          });

          if (error) {
            console.warn('[RevisionTracking] Rescore failed, using client estimate:', error.message);
            onTrackingError?.(new Error(error.message), 'rescoreAnalysis');
            trackEvent('Revision_Rescore_Error', {
              analysis_id: analysisId,
              error_code: error.code,
              error_message: error.message,
              tier,
            });
            return;
          }

          if (data?.success && data.score !== undefined && data.category) {
            serverScoreRef.current = Math.round(data.score);
            serverCategoryRef.current = data.category;

            trackEvent('Revision_Rescore_Success', {
              analysis_id: analysisId,
              original_score: originalScore,
              client_estimate: calculateRevisedScore(
                originalScore,
                acceptedIssueCount,
                totalIssues
              ),
              server_score: data.score,
              category: data.category,
              tier,
            });
          }
        } catch (err) {
          // SC4: Rescore errors non-blocking — UI continues with client-side estimate
          console.warn('[RevisionTracking] Rescore exception, using client estimate:', err);
          const { error: normalizedErr, message: errorMessage } = normalizeError(err);
          onTrackingError?.(normalizedErr, 'rescoreAnalysis');
          trackEvent('Revision_Rescore_Error', {
            analysis_id: analysisId,
            error_message: errorMessage,
            tier,
          });
        } finally {
          isRescoringRef.current = false;
          setIsRescoring(false);
        }
      }, RESCORE_DEBOUNCE_MS);
    },
    [enabled, analysisId, originalScore, context, acceptedIssueCount, totalIssues, tier, onTrackingError, serverScoreRef, serverCategoryRef]
  );

  return { triggerRescore, isRescoring };
}
